"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { CompanyDetailView } from "./company-detail-view"
import { CommunityToolbar, CompaniesTable } from "./graphite-community-tables"
import { toCompanyRow } from "@/hooks/use-community"
import { asArray } from "@/lib/as-array"
import { useGetCompanies, useGetCompaniesJobContactCounts  } from "openapi/api/endpoints"
import { Company, GetCompanies200 } from "openapi/api/model"
import CommunityViewRoute from "@/pages/community/page"
import { CreateCompanyForm } from "./create-company-form"
import { useUrlSyncedSearchState } from "@/hooks/use-url-synced-search-state"


export type CompanyeSearchParams={
  companyId?: string
  companyTab?: "contacts" | "jobs"
  createCompany?: true
  createContact?: true
}

export function CompaniesTab() {
  const [searchTerm, setSearchTerm] = useState("")
  const { data, error, isLoading } = useGetCompanies()
  const navigate = CommunityViewRoute.useNavigate()
  const search = CommunityViewRoute.useSearch()
  const { patchSearch, clearSearchKeys } = useUrlSyncedSearchState<CompanyeSearchParams>(navigate)
  const { companyId } = search as CompanyeSearchParams
  const isCreateDialogOpen = !!search.createCompany

  const openCompany = (id: string) => {
    patchSearch((prev) => ({
      companyId: id,
      companyTab: prev?.companyTab ?? "jobs",
      createCompany: undefined,
    }))
  }

  const closeCompany = () => {
    clearSearchKeys(["companyId", "companyTab", "createContact"])
  }

  const openCreateCompany = () => {
    patchSearch({ createCompany: true })
  }

  const closeCreateCompany = () => {
    clearSearchKeys(["createCompany"])
  }

  // create companiesList - even if empty
  const companiesList = (data as GetCompanies200)?.items ?? []
  const companyIds = companiesList.map(c => c.id);

  // Use enabled to control when it actually fetches
  const { data: countsData, isLoading: isLoadingCounts } = useGetCompaniesJobContactCounts(
    { companyIds: companyIds }, // Pass empty array on first render, that's fine
    {
      query: {
        enabled: companyIds.length > 0, // Don't fetch until we have IDs
      }
    }
  );

  // Map counts back to companies
  const companiesWithCounts = companiesList.map((company) => {
    /*
      `asArray` rather than `counts?.find`. The counts arrive from a second
      request, and anything non-array coming back — an error envelope, a
      wrapper, a contract that moved — threw straight out of `.find`. This runs
      inside the app shell, so that throw reached the error boundary and
      replaced the whole Community page with "Something went wrong!" rather
      than a table carrying zeroes in two columns.
    */
    const companyCount = asArray<{
      company_id?: string
      jobs_count?: number
      contacts_count?: number
    }>(countsData?.counts).find((c) => c.company_id === company.id);
    return {
      ...company,
      jobs_count: companyCount?.jobs_count || 0,
      contacts_count: companyCount?.contacts_count || 0,
    };
  });

  const needle = searchTerm.trim().toLowerCase()
  const rows = companiesWithCounts
    .filter((company) =>
      !needle ||
      [company.name, company.industry, company.location, company.website].some((field) =>
        (field ?? "").toLowerCase().includes(needle),
      ),
    )
    .map((company) => toCompanyRow(company, company))


    if (companyId) {
      return <CompanyDetailView companyId={companyId} onBack={closeCompany} />
    }
  

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        17a: the source rendered companies as a three-column card grid, which
        fits nine and hides the two numbers the page exists for. As rows, seven
        fit in the same pane and contacts and jobs read at a glance.
      */}
      <CommunityToolbar
        placeholder="Search companies"
        value={searchTerm}
        onChange={setSearchTerm}
        action="Create Company"
        onAction={openCreateCompany}
      />

      {error ? (
        <p className="py-6 text-[12.5px] text-muted-foreground">
          Companies could not be loaded.
        </p>
      ) : (
        <CompaniesTable
          rows={rows}
          /*
            Contacts and jobs come from a second query. Leaving the loading
            state when only the first has landed prints "0 contacts, 0 jobs"
            against every company — a real-looking answer to the question the
            table exists to answer.
          */
          isLoading={isLoading || isLoadingCounts}
          onOpen={openCompany}
        />
      )}

      <DialogShell
        open={isCreateDialogOpen}
        onOpenChange={(open) => (open ? openCreateCompany() : closeCreateCompany())}
        wide
        title="Create New Company"
      >
        {/* The form owns its own actions, so the shell takes no footer. */}
        <CreateCompanyForm
          onCancel={closeCreateCompany}
          onCreated={(company: Company) => {
            closeCreateCompany()
            openCompany(company.id)
          }}
        />
      </DialogShell>
    </div>
  )
}
